import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Card, CardContent, Chip, CircularProgress, Pagination, FormControl, InputLabel, Select, MenuItem, Avatar, TextField } from '@mui/material';
import {
  Search as SearchIcon,
  Person,
  Business,
  LocationOn,
  MedicalServices,
} from '@mui/icons-material';
import SearchBar from '../components/SearchBar';

interface SearchResult {
  npi: string;
  type: number;
  name: string;
  credential?: string;
  specialty?: string;
  location: {
    city?: string;
    state?: string;
  };
  gender?: string;
}

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [specialty, setSpecialty] = useState(searchParams.get('specialty') || '');
  const [state, setState] = useState(searchParams.get('state') || '');
  const [entityType, setEntityType] = useState(searchParams.get('type') || '');

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
  const LIMIT = 20;

  useEffect(() => {
    performSearch();
  }, [searchParams, page]);

  const performSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const q = searchParams.get('q');
      if (q) params.append('q', q);
      if (specialty) params.append('specialty', specialty);
      if (state) params.append('state', state);
      if (entityType) params.append('type', entityType);
      params.append('limit', LIMIT.toString());
      params.append('offset', ((page - 1) * LIMIT).toString());

      const response = await fetch(`${API_URL}/api/search?${params}`);
      const data = await response.json();

      setResults(data.results || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    const q = searchParams.get('q');
    if (q) params.append('q', q);
    if (specialty) params.append('specialty', specialty);
    if (state) params.append('state', state);
    if (entityType) params.append('type', entityType);

    setSearchParams(params);
    setPage(1);
  };

  const handleProfileClick = (result: SearchResult) => {
    // For now, navigate to NPI-based URL
    // In production, use custom username if claimed
    navigate(`/provider/${result.npi}`);
  };

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Search Medical Professionals
      </Typography>

      {/* Search Form */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gap: 2 }}>
              <SearchBar placeholder="Search by name, +handle, specialty, NPI, or location…" />

              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
                <TextField
                  label="Specialty"
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  placeholder="e.g., Cardiology"
                />
                <FormControl>
                  <InputLabel>State</InputLabel>
                  <Select value={state} onChange={(e) => setState(e.target.value)} label="State">
                    <MenuItem value="">All States</MenuItem>
                    {states.map((st) => (
                      <MenuItem key={st} value={st}>{st}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <InputLabel>Type</InputLabel>
                  <Select value={entityType} onChange={(e) => setEntityType(e.target.value)} label="Type">
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="1">Individual</MenuItem>
                    <MenuItem value="2">Organization</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Button
                onClick={handleSearch}
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                disabled={!specialty && !state && !entityType}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Apply filters
              </Button>
            </Box>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : results.length > 0 ? (
        <>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Found {total} results
          </Typography>

          <Box sx={{ display: 'grid', gap: 2 }}>
            {results.map((result) => (
              <Card key={result.npi}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 3,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s',
                  }}
                  onClick={() => handleProfileClick(result)}
                >
                  <CardContent>
                    <Box display="flex" alignItems="flex-start" gap={2}>
                      <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                        {result.type === 1 ? <Person /> : <Business />}
                      </Avatar>

                      <Box flex={1}>
                        <Typography variant="h6" component="div">
                          {result.name}
                          {result.credential && (
                            <Chip
                              label={result.credential}
                              size="small"
                              sx={{ ml: 1 }}
                              color="primary"
                            />
                          )}
                        </Typography>

                        <Box display="flex" gap={2} mt={1}>
                          {result.specialty && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <MedicalServices fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {result.specialty}
                              </Typography>
                            </Box>
                          )}

                          {result.location.city && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <LocationOn fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {result.location.city}, {result.location.state}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          NPI: {result.npi}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
            ))}
          </Box>

          {total > LIMIT && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={Math.ceil(total / LIMIT)}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      ) : searchParams.toString() ? (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            No results found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria
          </Typography>
        </Box>
      ) : null}
    </Container>
  );
};

export default SearchPage;
